import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useMountedGuard from 'Hooks/useMountedGuard';

const FQAdminContext = React.createContext(undefined);

export const CHOOSE_LIST = 'choose-list';

const MAP_RELATIONSHIP_NAME_TO_CREATE_RELATIONSHIP_NAME = {
  [fr.relationships.quizAdminItems.name]: fr.relationships.quizzes.name,
  [fr.relationships.quizzes.name]: fr.relationships.quizzes.name,
  [fr.relationships.sectionAdminItems.name]: fr.relationships.sections.name,
  [fr.relationships.sections.name]: fr.relationships.sections.name,
  [fr.relationships.questionAdminItems.name]: fr.relationships.questions.name,
  [fr.relationships.questions.name]: fr.relationships.questions.name,
  [fr.relationships.insightAdminItems.name]: fr.relationships.insights.name,
  [fr.relationships.insights.name]: fr.relationships.insights.name,
  [fr.relationships.conditionAdminItems.name]: fr.relationships.conditions.name,
  [fr.relationships.conditions.name]: fr.relationships.conditions.name,
  [fr.relationships.experiments.name]: fr.relationships.experiments.name,
  [fr.relationships.routeWhitelist.name]: fr.relationships.routeWhitelist.name,
  [fr.relationships.contactFormAdminItems.name]: fr.relationships.contactForms.name,
  [fr.relationships.paymentPages.name]: fr.relationships.paymentPages.name,
  [fr.relationships.completedScreens.name]: fr.relationships.completedScreens.name,
  [fr.relationships.banners.name]: fr.relationships.banners.name,

};

const init = (adminPageResource) => {
  const getRelName = (resource, relName) => resource.withRelationship(fr.relationships.quizAdminItems.name, () => relName, () => null);

  const initRelationships = [
    getRelName(adminPageResource, fr.relationships.quizAdminItems.name),
    getRelName(adminPageResource, fr.relationships.sectionAdminItems.name),
    getRelName(adminPageResource, fr.relationships.questionAdminItems.name),
    getRelName(adminPageResource, fr.relationships.insightAdminItems.name),
    getRelName(adminPageResource, fr.relationships.conditionAdminItems.name),
    getRelName(adminPageResource, fr.relationships.experiments.name),
    getRelName(adminPageResource, fr.relationships.routeWhitelist.name),
    getRelName(adminPageResource, fr.relationships.contactFormAdminItems.name),
    getRelName(adminPageResource, fr.relationships.paymentPages.name),
    getRelName(adminPageResource, fr.relationships.completedScreens.name),
    getRelName(adminPageResource, fr.relationships.banners.name),
  ].filter((v) => !!v);

  if (initRelationships.length === 0) {
    throw new Error('Unexpected no available relationships');
  }

  return {
    listRelationshipName: initRelationships[0],
    createRelationshipName: MAP_RELATIONSHIP_NAME_TO_CREATE_RELATIONSHIP_NAME[initRelationships[0]],
    lozengeRelationshipNames: initRelationships,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case CHOOSE_LIST: {
      return {
        ...state,
        listRelationshipName: action.listRelationshipName,
        createRelationshipName: MAP_RELATIONSHIP_NAME_TO_CREATE_RELATIONSHIP_NAME[action.listRelationshipName],
      };
    }
    default: {
      throw new Error(`Unhandled NewLoginContext action type: ${action.type}`);
    }
  }
};

const FQAdminProvider = ({
  pageContentResource,
  children,
}) => {
  const [state, _dispatch] = React.useReducer(reducer, pageContentResource, init);
  const { doWithMountedGuard } = useMountedGuard();
  const dispatch = doWithMountedGuard(_dispatch);

  const setListChoice = React.useCallback((listRelName) => {
    dispatch({ type: CHOOSE_LIST, listRelationshipName: listRelName });
  }, [dispatch]);

  const memoizedValue = React.useMemo(() => {
    return {
      listPrompt: pageContentResource.withRelationship(state.listRelationshipName, (rel) => {
        const prompt = rel.meta('prompt');
        if (!prompt) {
          throw new Error(`Missing prompt for rel ${rel.attr('name')}`);
        }

        return rel.meta('prompt');
      }, () => {
        throw new Error(`List relationship ${state.listRelationshipName} not found on page resource`);
      }),
      listPromptSingular: pageContentResource.withRelationship(state.listRelationshipName, (rel) => {
        const prompt = rel.meta('promptSingular');
        if (!prompt) {
          throw new Error(`Missing singular prompt for rel ${rel.attr('name')}`);
        }

        return rel.meta('promptSingular');
      }, () => {
        throw new Error(`List relationship ${state.listRelationshipName} not found on page resource`);
      }),
      listRelationshipName: state.listRelationshipName,
      isListItemSelected: (relName) => relName === state.listRelationshipName,
      setListChoice,
      createRelationshipName: state.createRelationshipName,
      lozengeRelationshipNames: state.lozengeRelationshipNames,
    };
  }, [
    pageContentResource,
    state,
    setListChoice,
  ]);

  return (
    <FQAdminContext.Provider value={memoizedValue}>
      {children}
    </FQAdminContext.Provider>
  );
};

// Use FQAdmin State Context
const useFQAdminContext = () => {
  const context = React.useContext(FQAdminContext);
  if (context === undefined) {
    throw new Error('useFQAdminContext must be used within a FQAdminProvider');
  }
  return context;
};

FQAdminProvider.propTypes = {
  pageContentResource: PropTypes.object.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  FQAdminProvider,
  useFQAdminContext,
};
